import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import {
  changePassword,
  isAuthenticated,
  isFederatedUser,
  signOut,
} from '../../services/cognitoService';
import { updateUserProfileApi } from '../../services/userService';
import SettingsAccount from './components/SettingsAccount';
import SettingsGeneral from './components/SettingsGeneral';
import SettingsPassword from './components/SettingsPassword';
import SettingsUsage from './components/SettingsUsage';
import './Settings.css';

type TabType = 'general' | 'password' | 'usage' | 'account';

/** Settings page — Claude-inspired sidebar layout with modular tab components */
const Settings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialAuthCheck] = useState(() => isAuthenticated());

  useEffect(() => {
    // 1. Initial check
    if (!isAuthenticated()) {
      signOut();
      navigate('/login', { state: { from: location.pathname + location.search }, replace: true });
      return;
    }

    // 2. Periodic check (covers manual token deletion in devtools and token expiration)
    const interval = setInterval(() => {
      if (!isAuthenticated()) {
        signOut();
        navigate('/', { replace: true });
      }
    }, 1000);

    // 3. Storage event listener (covers logouts/changes in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken' || e.key === 'idToken' || e.key === 'tokenExpiresAt') {
        if (!isAuthenticated()) {
          signOut();
          navigate('/', { replace: true });
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, location.pathname, location.search]);

  const isFederated = isFederatedUser();
  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  if (!initialAuthCheck) {
    signOut();
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  /** Handle saving general info (display name, given name, family name) */
  const handleSaveGeneral = async (fields: {
    display_name: string;
    given_name: string;
    family_name: string;
  }) => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await updateUserProfileApi(fields);
      setSuccessMessage(t('settings.saveSuccess', 'Changes saved successfully!'));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  /** Handle saving new password */
  const handleSavePassword = async (currentPassword: string, newPassword: string) => {
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const result = await changePassword(currentPassword, newPassword);
      if (result.success) {
        setSuccessMessage(t('settings.passwordSuccess', 'Password updated successfully!'));
      } else {
        setErrorMessage(result.message);
      }
    } catch (_error) {
      setErrorMessage(t('settings.passwordError', 'Failed to update password. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  /** Clear messages when cancelling */
  const handleCancel = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  /** Sidebar navigation items */
  const sidebarItems: { key: TabType; label: string; icon: React.ReactNode }[] = [
    {
      key: 'general',
      label: t('settings.general', 'General'),
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <title>General Icon</title>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    ...(!isFederated
      ? [
          {
            key: 'password' as TabType,
            label: t('settings.password', 'Password'),
            icon: (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>Password Icon</title>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            ),
          },
        ]
      : []),
    {
      key: 'usage',
      label: t('settings.usage', 'Usage'),
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <title>Usage Icon</title>
          <path d="M12 20V10" />
          <path d="M18 20V4" />
          <path d="M6 20v-4" />
        </svg>
      ),
    },
    {
      key: 'account',
      label: t('settings.account', 'Account'),
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <title>Account Icon</title>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
    },
  ];

  /** Render the active tab content */
  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <SettingsGeneral
            successMessage={successMessage}
            errorMessage={errorMessage}
            isLoading={isLoading}
            onSave={handleSaveGeneral}
            onCancel={handleCancel}
          />
        );
      case 'password':
        return (
          <SettingsPassword
            successMessage={successMessage}
            errorMessage={errorMessage}
            isLoading={isLoading}
            onSave={handleSavePassword}
            onCancel={handleCancel}
          />
        );
      case 'usage':
        return <SettingsUsage />;
      case 'account':
        return <SettingsAccount />;
      default:
        return null;
    }
  };

  return (
    <div className="settings-page-wrapper">
      <div className="settings-bg" />

      <div className="settings-page">
        <Header isLoggedIn={isAuthenticated()} />

        <main className="settings-main">
          <div className="settings-container">
            {/* Page Title */}
            <div className="settings-header">
              <div className="settings-title-wrapper">
                <span className="settings-title-line" />
                <h1 className="settings-title">{t('settings.accountSettings', 'Settings')}</h1>
                <span className="settings-title-line" />
              </div>
              <p className="settings-subtitle">
                {t('settings.manageProfile', 'Manage your profile & preferences')}
              </p>
            </div>

            {/* Sidebar + Content Layout */}
            <div className="settings-layout">
              {/* Sidebar Navigation */}
              <nav className="settings-sidebar" aria-label="Settings navigation">
                {sidebarItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`settings-sidebar-item ${activeTab === item.key ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab(item.key);
                      setErrorMessage('');
                      setSuccessMessage('');
                    }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>

              {/* Content Area */}
              <div className="settings-content">{renderTabContent()}</div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default Settings;
